import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import type { Portal } from "@/types";
import { FormProvider, TextBox } from "@/components/form";

const securitySchema = yup.object().shape({
  max_failed_login_attempts: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(1, "Minimum 1 attempt")
    .max(20, "Maximum 20 attempts"),
  lockout_duration_minutes: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(1, "Minimum 1 minute")
    .max(1440, "Maximum 1440 minutes (24 hours)"),
  password_min_length: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(6, "Minimum 6 characters")
    .max(32, "Maximum 32 characters"),
  password_require_uppercase: yup.boolean().required(),
  password_require_lowercase: yup.boolean().required(),
  password_require_number: yup.boolean().required(),
  password_require_special: yup.boolean().required(),
  password_history_count: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(0, "Minimum 0 (disabled)")
    .max(24, "Maximum 24 remembered passwords"),
  email_sender_email: yup.string().email("Must be a valid email").default(""),
  email_sender_name: yup.string().default(""),
  smtp_host: yup.string().default(""),
  smtp_port: yup
    .number()
    .typeError("Must be a number")
    .default(587)
    .min(1, "Invalid port")
    .max(65535, "Invalid port"),
  smtp_user: yup.string().default(""),
  smtp_password: yup.string().default(""),
  password_reset_token_expiry: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(300, "Minimum 5 minutes (300 seconds)")
    .max(86400, "Maximum 24 hours (86400 seconds)"),
  mfa_enforced: yup.boolean().required(),
  mfa_remember_days: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(0, "Minimum 0 (disabled)")
    .max(90, "Maximum 90 days"),
  mfa_app_name: yup.string().default(""),
});

export type SecurityFormValues = yup.InferType<typeof securitySchema>;

interface SecurityTabProps {
  portal: Portal;
  onSave: (data: SecurityFormValues) => void;
  isUpdating: boolean;
}

type SecurityTabKey = "mfa" | "lockout" | "password" | "history" | "email";

const SecurityTab: React.FC<SecurityTabProps> = ({ portal, onSave, isUpdating }) => {
  const [activeTab, setActiveTab] = useState<SecurityTabKey>("mfa");
  const methods = useForm<SecurityFormValues>({
    resolver: yupResolver(securitySchema),
    mode: "onChange",
    defaultValues: {
      max_failed_login_attempts: portal.settings?.max_failed_login_attempts ?? 5,
      lockout_duration_minutes: portal.settings?.lockout_duration_minutes ?? 30,
      password_min_length: portal.settings?.password_min_length ?? 8,
      password_require_uppercase: portal.settings?.password_require_uppercase ?? true,
      password_require_lowercase: portal.settings?.password_require_lowercase ?? true,
      password_require_number: portal.settings?.password_require_number ?? true,
      password_require_special: portal.settings?.password_require_special ?? true,
      password_history_count: portal.settings?.password_history_count ?? 3,
      email_sender_email: portal.settings?.email_sender_email ?? "",
      email_sender_name: portal.settings?.email_sender_name ?? "",
      smtp_host: portal.settings?.smtp_host ?? "",
      smtp_port: portal.settings?.smtp_port ?? 587,
      smtp_user: portal.settings?.smtp_user ?? "",
      smtp_password: "",
      password_reset_token_expiry: portal.settings?.password_reset_token_expiry ?? 900,
      mfa_enforced: portal.settings?.mfa_enforced ?? false,
      mfa_remember_days: portal.settings?.mfa_remember_days ?? 14,
      mfa_app_name: portal.settings?.mfa_app_name ?? "",
    },
  });

  useEffect(() => {
    methods.reset({
      max_failed_login_attempts: portal.settings?.max_failed_login_attempts ?? 5,
      lockout_duration_minutes: portal.settings?.lockout_duration_minutes ?? 30,
      password_min_length: portal.settings?.password_min_length ?? 8,
      password_require_uppercase: portal.settings?.password_require_uppercase ?? true,
      password_require_lowercase: portal.settings?.password_require_lowercase ?? true,
      password_require_number: portal.settings?.password_require_number ?? true,
      password_require_special: portal.settings?.password_require_special ?? true,
      password_history_count: portal.settings?.password_history_count ?? 3,
      email_sender_email: portal.settings?.email_sender_email ?? "",
      email_sender_name: portal.settings?.email_sender_name ?? "",
      smtp_host: portal.settings?.smtp_host ?? "",
      smtp_port: portal.settings?.smtp_port ?? 587,
      smtp_user: portal.settings?.smtp_user ?? "",
      smtp_password: "",
      password_reset_token_expiry: portal.settings?.password_reset_token_expiry ?? 900,
      mfa_enforced: portal.settings?.mfa_enforced ?? false,
      mfa_remember_days: portal.settings?.mfa_remember_days ?? 14,
      mfa_app_name: portal.settings?.mfa_app_name ?? "",
    });
  }, [portal, methods]);

  return (
    <FormProvider methods={methods} onSubmit={onSave}>
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val as SecurityTabKey)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="mfa" label="MFA" />
          <Tab value="lockout" label="Account Lockout" />
          <Tab value="password" label="Password Policy" />
          <Tab value="history" label="Password History" />
          <Tab value="email" label="Email & Reset" />
        </Tabs>
      </Box>

      <Stack spacing={2.5}>
        {activeTab === "mfa" && (
          <>
            <Typography variant="body2" color="text.secondary">
              When enforced, all portal admins and chat users in this portal must
              set up MFA on their next sign-in. Users can also opt-in voluntarily
              from their Security page.
            </Typography>
            <Controller
              name="mfa_enforced"
              control={methods.control}
              render={({ field }) => (
                <FormControlLabel
                  labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isUpdating}
                      size="small"
                    />
                  }
                  label="Enforce MFA for all users in this portal"
                />
              )}
            />

            <TextBox
              name="mfa_remember_days"
              label="Remember device (days)"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 0, max: 90 } }}
              helperText="How many days a device stays trusted after MFA verification (0 to disable)"
            />

            <TextBox
              name="mfa_app_name"
              label="Authenticator app name"
              fullWidth
              disabled={isUpdating}
              helperText="Issuer name shown in the user's authenticator app (e.g. Verve VIPS)"
            />
          </>
        )}

        {activeTab === "lockout" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Configure brute force protection. When a user exceeds the maximum
              failed login attempts, their account will be locked for the specified
              duration.
            </Typography>
            <TextBox
              name="max_failed_login_attempts"
              label="Max failed login attempts"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 1, max: 20 } }}
            />
            <TextBox
              name="lockout_duration_minutes"
              label="Lockout duration (minutes)"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 1, max: 1440 } }}
            />
          </>
        )}

        {activeTab === "password" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Configure password complexity requirements for this portal's users.
            </Typography>
            <TextBox
              name="password_min_length"
              label="Minimum password length"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 6, max: 32 } }}
            />
            <Stack spacing={1}>
              <Controller
                name="password_require_uppercase"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                    control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                    label="Require uppercase letter (A-Z)"
                  />
                )}
              />
              <Controller
                name="password_require_lowercase"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                    control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                    label="Require lowercase letter (a-z)"
                  />
                )}
              />
              <Controller
                name="password_require_number"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                    control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                    label="Require number (0-9)"
                  />
                )}
              />
              <Controller
                name="password_require_special"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                    control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                    label="Require special character (!@#$%^&*)"
                  />
                )}
              />
            </Stack>
          </>
        )}

        {activeTab === "history" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Prevent users from reusing recent passwords when changing their
              password.
            </Typography>
            <TextBox
              name="password_history_count"
              label="Remembered passwords"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 0, max: 24 } }}
            />
          </>
        )}

        {activeTab === "email" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Configure SMTP settings for sending password reset emails.
              Credentials are stored securely in AWS Secrets Manager.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 2,
                rowGap: 2,
              }}
            >
              <TextBox
                name="email_sender_email"
                label="Sender email address"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. noreply@yourportal.com"
              />
              <TextBox
                name="email_sender_name"
                label="Sender display name"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. CACI Portal"
              />
              <TextBox
                name="smtp_host"
                label="SMTP host"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. smtp.gmail.com"
              />
              <TextBox
                name="smtp_port"
                label="SMTP port"
                type="number"
                fullWidth
                disabled={isUpdating}
                slotProps={{ htmlInput: { min: 1, max: 65535 } }}
              />
              <TextBox
                name="smtp_user"
                label="SMTP username"
                fullWidth
                disabled={isUpdating}
                helperText="Email or username for SMTP authentication"
              />
              <TextBox
                name="smtp_password"
                label="SMTP password"
                type="password"
                fullWidth
                disabled={isUpdating}
                helperText="Leave empty to keep existing password unchanged"
              />
              <TextBox
                name="password_reset_token_expiry"
                label="Reset link expiry (seconds)"
                type="number"
                fullWidth
                disabled={isUpdating}
                slotProps={{ htmlInput: { min: 300, max: 86400 } }}
                helperText="How long the reset link stays valid (default: 900 = 15 min)"
              />
            </Box>
          </>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isUpdating || !methods.formState.isValid}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save Security Settings"}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default SecurityTab;
